<script setup lang="ts">
import { ref, computed } from "vue";
import { useMutation } from "@vue/apollo-composable";
import { gql } from "@apollo/client/core";
import { ClientError, getMessageFromError } from "@/libs/errors";

const loading = ref(false);
const errorMessage = ref("");
const successMessage = ref("");
const email = ref("");
const regNumber = ref("");
const reason = ref("");
const personType = ref<"NATURAL_PERSON" | "LEGAL_PERSON">("NATURAL_PERSON");

const regNumberMask = computed(() =>
  personType.value === "NATURAL_PERSON"
    ? "###.###.###-##"
    : "##.###.###/####-##"
);

const { mutate: requestAccountDeletion } = useMutation(
  gql`
    mutation RequestAccountDeletion($input: RequestAccountDeletionInput!) {
      requestAccountDeletion(input: $input) {
        id
        status
        createdAt
        originalEmail
      }
    }
  `,
  {
    fetchPolicy: "no-cache",
  }
);

const handleSubmit = async () => {
  if (loading.value) return;

  errorMessage.value = "";
  successMessage.value = "";
  loading.value = true;

  try {
    if (!email.value || !email.value.includes("@")) {
      throw new ClientError("Por favor, informe um e-mail válido.");
    }

    const regNumberClean = regNumber.value.replace(/\D/g, "");
    if (regNumberClean.length < 11) {
      throw new ClientError("Por favor, informe um CPF ou CNPJ válido.");
    }

    await requestAccountDeletion({
      input: {
        email: email.value.trim().toLowerCase(),
        regNumber: regNumberClean,
        reason: reason.value.trim() || null,
      },
    });

    successMessage.value =
      "Sua solicitação foi registrada com sucesso. Nossa equipe irá analisar e você será notificado por email.";

    email.value = "";
    regNumber.value = "";
    reason.value = "";
  } catch (err) {
    errorMessage.value = getMessageFromError(err);
  } finally {
    loading.value = false;
  }
};
</script>

<template>
  <div class="legal-page">
    <div class="legal-container">
      <nav class="legal-nav">
        <router-link to="/auth/login" class="back-link">
          <svg
            xmlns="http://www.w3.org/2000/svg"
            width="16"
            height="16"
            fill="currentColor"
            viewBox="0 0 16 16"
          >
            <path
              fill-rule="evenodd"
              d="M15 8a.5.5 0 0 0-.5-.5H2.707l3.147-3.146a.5.5 0 1 0-.708-.708l-4 4a.5.5 0 0 0 0 .708l4 4a.5.5 0 0 0 .708-.708L2.707 8.5H14.5A.5.5 0 0 0 15 8z"
            />
          </svg>
          Voltar
        </router-link>
        <img src="@/assets/images/logo.svg" alt="Quantofaz" class="logo" />
      </nav>

      <article class="legal-content">
        <h1>Solicitar Exclusão de Conta</h1>

        <div v-if="successMessage" class="alert alert-success" role="alert">
          {{ successMessage }}
        </div>

        <template v-else>
          <div class="alert alert-warning mb-4">
            <strong>Atenção:</strong> A exclusão da sua conta é permanente e
            irreversível.
            <ul class="mb-0 mt-2">
              <li>Todos os seus dados pessoais serão removidos</li>
              <li>Seu histórico de uso será apagado</li>
              <li>Assinaturas ativas serão canceladas</li>
              <li>Você perderá acesso a todos os recursos da plataforma</li>
            </ul>
          </div>

          <p class="mb-4">
            Se você possui uma assinatura ativa, por favor cancele-a antes de
            solicitar a exclusão da conta.
          </p>

          <form @submit.prevent="handleSubmit" class="deletion-form">
            <div v-if="errorMessage" class="alert alert-danger" role="alert">
              {{ errorMessage }}
            </div>

            <div class="form-group mb-3">
              <label for="email" class="form-label">E-mail da conta</label>
              <input
                id="email"
                type="email"
                class="form-control"
                v-model="email"
                placeholder="Digite o e-mail cadastrado"
                required
              />
            </div>

            <div class="person-type mb-3">
              <label class="form-label d-block">Tipo de documento</label>
              <div class="form-check form-check-inline">
                <input
                  class="form-check-input"
                  type="radio"
                  name="personType"
                  id="person-type-cpf"
                  value="NATURAL_PERSON"
                  v-model="personType"
                />
                <label class="form-check-label" for="person-type-cpf">
                  CPF
                </label>
              </div>
              <div class="form-check form-check-inline">
                <input
                  class="form-check-input"
                  type="radio"
                  name="personType"
                  id="person-type-cnpj"
                  value="LEGAL_PERSON"
                  v-model="personType"
                />
                <label class="form-check-label" for="person-type-cnpj">
                  CNPJ
                </label>
              </div>
            </div>

            <div class="form-group mb-3">
              <label for="reg-number" class="form-label">
                {{ personType === "NATURAL_PERSON" ? "CPF" : "CNPJ" }}
              </label>
              <input
                id="reg-number"
                type="text"
                class="form-control"
                v-model="regNumber"
                v-maska="regNumberMask"
                :placeholder="
                  personType === 'NATURAL_PERSON'
                    ? '000.000.000-00'
                    : '00.000.000/0000-00'
                "
                required
              />
            </div>

            <div class="form-group mb-4">
              <label for="reason" class="form-label">
                Motivo da solicitação (opcional)
              </label>
              <textarea
                id="reason"
                class="form-control"
                v-model="reason"
                rows="3"
                maxlength="500"
                placeholder="Conte-nos por que deseja excluir sua conta..."
              ></textarea>
              <small class="form-text text-muted">
                {{ reason.length }}/500 caracteres
              </small>
            </div>

            <button
              type="submit"
              class="btn btn-danger btn-block"
              :disabled="loading"
            >
              <span
                v-if="loading"
                class="spinner-border spinner-border-sm me-2"
              ></span>
              {{ loading ? "Enviando..." : "Solicitar Exclusão" }}
            </button>
          </form>
        </template>
      </article>
    </div>
  </div>
</template>

<style scoped>
.legal-page {
  min-height: 100vh;
  background-color: var(--black-eigengrau);
  color: var(--white-ice);
}

.legal-container {
  max-width: 600px;
  margin: 0 auto;
  padding: 2rem 1.5rem 4rem;
}

.legal-nav {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 3rem;
  padding-bottom: 1.5rem;
  border-bottom: 1px solid var(--dark-grey-alpha);
}

.back-link {
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  color: var(--primary-color);
  text-decoration: none;
  font-weight: 500;
  transition: opacity 0.2s;
}

.back-link:hover {
  opacity: 0.8;
}

.logo {
  height: 32px;
}

.legal-content h1 {
  font-size: 1.75rem;
  font-weight: 700;
  color: var(--primary-color);
  margin-bottom: 1.5rem;
}

.deletion-form {
  background: var(--dark-color);
  padding: 1.5rem;
  border-radius: 8px;
}

.form-label {
  font-weight: 500;
  color: var(--white-ice);
}

.form-control {
  background: var(--black-eigengrau);
  border: 1px solid var(--dark-grey-alpha);
  color: var(--white-ice);
}

.form-control:focus {
  background: var(--black-eigengrau);
  border-color: var(--primary-color);
  color: var(--white-ice);
  box-shadow: 0 0 0 0.2rem rgba(166, 199, 20, 0.25);
}

.form-control::placeholder {
  color: var(--grey-color);
}

.form-check-label {
  color: var(--white-ice);
}

.form-check-input:checked {
  background-color: var(--primary-color);
  border-color: var(--primary-color);
}

.btn-block {
  width: 100%;
}

.btn-danger {
  background-color: #dc3545;
  border-color: #dc3545;
}

.btn-danger:hover:not(:disabled) {
  background-color: #bb2d3b;
  border-color: #b02a37;
}

.btn-danger:disabled {
  opacity: 0.65;
}

.alert-warning {
  background-color: rgba(255, 193, 7, 0.15);
  border-color: rgba(255, 193, 7, 0.3);
  color: #ffc107;
}

.alert-warning ul {
  padding-left: 1.25rem;
}

.alert-success {
  background-color: rgba(25, 135, 84, 0.15);
  border-color: rgba(25, 135, 84, 0.3);
  color: #198754;
}

.alert-danger {
  background-color: rgba(220, 53, 69, 0.15);
  border-color: rgba(220, 53, 69, 0.3);
  color: #dc3545;
}

.text-muted {
  color: var(--grey-color) !important;
}

@media (max-width: 576px) {
  .legal-container {
    padding: 1.5rem 1rem 3rem;
  }

  .legal-nav {
    margin-bottom: 2rem;
  }

  .legal-content h1 {
    font-size: 1.5rem;
  }

  .logo {
    height: 24px;
  }

  .deletion-form {
    padding: 1rem;
  }
}
</style>
